import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import * as z from "zod";
import { Brand } from "@/types/brand";
import { PlusCircle, Trash2 } from "lucide-react";

const formSchema = z.object({
  products: z.array(
    z.object({
      productName: z.string().min(1, "Product title is required"),
      productUrl: z.string().url("Please enter a valid URL"),
    })
  ),
});

type FormValues = z.infer<typeof formSchema>;

interface ProductsServicesFormProps {
  data: Brand;
  onSubmit: (data: Partial<Brand>) => void;
  mode: "onboarding" | "edit";
  onBack?: () => void; // Optional for onboarding mode
}

export function ProductsServicesForm({
  data,
  onSubmit,
  mode,
  onBack,
}: ProductsServicesFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      products:
        data.products && data.products.length > 0
          ? data.products
          : [{ productName: "", productUrl: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "products",
  });

  const handleFormSubmit = (values: FormValues) => {
    onSubmit(values);
  };

  const renderTitle = () => {
    if (mode === "onboarding") {
      return (
        <>
          <CardTitle>Products & Services</CardTitle>
          <CardDescription>
            Add your key products or services to help us create targeted content
          </CardDescription>
        </>
      );
    }
    return null; // In edit mode, the title is handled by the parent component
  };

  const renderButtons = () => {
    if (mode === "onboarding") {
      return (
        <div className="flex justify-between pt-4">
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="submit">Continue</Button>
        </div>
      );
    }

    return (
      <div className="flex justify-end pt-4">
        <Button type="submit" className="bg-black hover:bg-black/90">
          Save
        </Button>
      </div>
    );
  };

  const formContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6"
      >
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-start space-x-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                <FormField
                  control={form.control}
                  name={`products.${index}.productName`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={index !== 0 ? "sr-only" : ""}>
                        Product/Service Title
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Product Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`products.${index}.productUrl`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={index !== 0 ? "sr-only" : ""}>
                        Product/Service URL
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com/product"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div
                className="flex"
                style={{ marginTop: index === 0 ? "2rem" : "0.5rem" }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => append({ productName: "", productUrl: "" })}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Another Product/Service
        </Button>

        <FormDescription className="text-center mt-4">
          Adding your products helps us create content that drives conversions
        </FormDescription>

        {renderButtons()}
      </form>
    </Form>
  );

  // In onboarding mode, wrap in Card, in edit mode, return just the form
  if (mode === "onboarding") {
    return (
      <Card className="w-full max-w-3xl mx-auto overflow-hidden">
        <CardHeader>{renderTitle()}</CardHeader>
        <CardContent>{formContent}</CardContent>
      </Card>
    );
  }

  return formContent;
}
